import React, { useState, useEffect, useCallback } from 'react';
import { useZxing } from 'react-zxing';
import { useField } from 'formik';
import { Skeleton } from 'components/shadcn/ui/skeleton';

// Import Shadcn components
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from 'components/shadcn/ui/dialog';
import { Label } from 'components/shadcn/ui/label';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from 'components/shadcn/ui/select';
import { Button } from 'components/shadcn/ui/button';
import { useToast } from 'components/shadcn/hooks/use-toast';
import { X } from 'lucide-react';

const HGBarcodeScanner = ({ modalOpen, handleClose, fieldName }) => {
    const [devices, setDevices] = useState([]);
    const [selectedDevice, setSelectedDevice] = useState('');
    const [code, setCode] = useState('');
    const [camStream, setCamStream] = useState(null);
    const [helpers] = useField(fieldName);
    const { toast } = useToast();

    const { ref } = useZxing({
        onDecodeResult(newScan) {
            setCode(newScan.getText());
        },
        deviceId: selectedDevice,
    });

    const handleDeviceChange = (value) => {
        setSelectedDevice(value);
    };

    const stopStream = useCallback(() => {
        if (camStream && ref.current) {
            camStream.getTracks().forEach((track) => track.stop());
            setSelectedDevice('');
            ref.current.srcObject = null;
            setCamStream(null);
        }
    }, [camStream, ref]);

    const closeModal = useCallback(() => {
        stopStream();
        handleClose();
    }, [handleClose, stopStream]);

    useEffect(() => {
        if (code.length > 0) {
            helpers.setValue(code);
            closeModal();
        }
    }, [code, helpers, closeModal]);

    useEffect(() => {
        const getCamStream = async () => {
            try {
                const stream = await navigator.mediaDevices.getUserMedia({
                    video: true,
                });
                if (stream?.active) {
                    const availableDevices =
                        await navigator.mediaDevices.enumerateDevices();
                    const availableVideoDevices = availableDevices.filter(
                        (device) => device.kind === 'videoinput'
                    );
                    if (
                        availableVideoDevices.length === 0 ||
                        !availableVideoDevices[0].deviceId
                    ) {
                        toast({
                            title: 'Barcode Scanner Error',
                            description: 'No cameras found',
                            variant: 'destructive',
                        });
                        return;
                    }
                    setCamStream(stream);
                    setDevices(availableVideoDevices);
                }
            } catch (error) {
                console.log('barcode scanner error: ', error);
                if (error.name === 'NotAllowedError') {
                    toast({
                        title: 'Barcode Scanner Error',
                        description:
                            'Camera access denied. Please enable it in your browser settings.',
                        variant: 'destructive',
                    });
                    setTimeout(() => {
                        closeModal();
                    }, 3000);
                } else {
                    toast({
                        title: 'Barcode Scanner Error',
                        description: error.message || 'Unknown error occurred',
                        variant: 'destructive',
                    });
                }
            }
        };
        if (modalOpen && !camStream) getCamStream();
        return () => {
            stopStream();
        };
    }, [modalOpen, stopStream, camStream, closeModal, toast]);

    return (
        <Dialog open={modalOpen} onOpenChange={closeModal}>
            <DialogContent className="sm:max-w-md">
                <DialogHeader>
                    <DialogTitle>
                        {!camStream?.active
                            ? 'Requesting camera access'
                            : 'Barcode Scanner'}
                    </DialogTitle>
                </DialogHeader>

                <Button
                    variant="ghost"
                    className="absolute right-2 top-2 h-8 w-8 p-0"
                    onClick={closeModal}
                >
                    <X className="h-4 w-4" />
                </Button>

                {!camStream?.active ? (
                    <div className="space-y-4 p-6">
                        <Skeleton className="h-10 w-full" />
                        <Skeleton className="aspect-square w-full" />
                    </div>
                ) : (
                    <div className="space-y-4">
                        <div>
                            <Label htmlFor="camera-select">Select Camera</Label>
                            <Select
                                value={selectedDevice}
                                onValueChange={handleDeviceChange}
                            >
                                <SelectTrigger
                                    id="camera-select"
                                    className="w-full"
                                >
                                    <SelectValue placeholder="Select a camera" />
                                </SelectTrigger>
                                <SelectContent>
                                    {devices.map((device, index) => (
                                        <SelectItem
                                            key={index}
                                            value={device.deviceId}
                                        >
                                            {device.label ||
                                                `Device ${index + 1}`}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="relative overflow-hidden">
                            <video
                                className="w-full border border-border"
                                ref={ref}
                            />
                            <div className="absolute left-[5%] top-[20%] z-10 h-[60%] w-[90%] border border-blue-500" />
                        </div>
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
};

export default HGBarcodeScanner;
